import { z } from 'zod';

import type { OrganizationResponse, OrganizationUpdate } from '@/api/generated';
import { isFieldDirty, type DirtyFieldMap } from '@/lib/dirtyFields';
import {
  contactPart,
  contactToPayload,
  emailField,
  keepStoredAddress,
  optionalText,
} from '@/lib/organization';

/**
 * The commercial half of an organization account: what was agreed, what it
 * costs, and where the invoicing stands.
 *
 * The rules here mirror the backend's rather than inventing a second set, so a
 * value is never accepted on this screen and refused by the server. Each one was
 * checked against the running backend, and the messages it gives are recorded in
 * the tests.
 */

/** What the backend uses when an account has never been given one. */
export const DEFAULT_CURRENCY = 'USD';

/** The four billing-contact parts, which are saved and cleared as one thing. */
const CONTACT_FIELDS = [
  'billing_contact_name',
  'billing_contact_title',
  'billing_contact_email',
  'billing_contact_phone',
] as const;

/**
 * One sentence for the one rule, wherever it is caught.
 *
 * The backend refuses this in two places with two different wordings, and one
 * of them arrives as "Body: Value error, contract_end_date cannot be earlier
 * than contract_start_date". Staff see this sentence instead, whichever of the
 * two refused it, so the same mistake never reads two ways.
 */
export const CONTRACT_DATES_MESSAGE = 'A contract cannot end before it starts.';

export const billingFormSchema = z
  .object({
    plan_name: z
      .string()
      .trim()
      .optional()
      .transform((value) => (value === '' ? undefined : value))
      .refine(
        (value) => value === undefined || value.length <= 100,
        'Use 100 characters or fewer',
      ),

    /*
     * Text, not a number. The backend stores a decimal, and putting the amount
     * through a JavaScript number on the way there is how 1500.10 becomes
     * 1500.0999999999999.
     */
    plan_cost: z
      .string()
      .trim()
      .optional()
      .transform((value) => (value === '' ? undefined : value))
      .refine(
        (value) => value === undefined || /^\d+(\.\d{1,2})?$/.test(value),
        'Enter an amount, with at most two decimal places and no minus sign',
      ),

    plan_currency: z
      .string()
      .trim()
      .toUpperCase()
      .min(1, 'Enter a currency code')
      .refine((value) => /^[A-Z]{3}$/.test(value), 'Use a three-letter code, like USD'),

    plan_billing_cycle: z.string().optional(),

    contract_start_date: optionalText(),
    contract_end_date: optionalText(),
    renewal_date: optionalText(),

    invoice_status: z.string().optional(),
    payment_status: z.string().optional(),

    last_invoice_number: z
      .string()
      .trim()
      .optional()
      .transform((value) => (value === '' ? undefined : value))
      .refine(
        (value) => value === undefined || value.length <= 64,
        'Use 64 characters or fewer',
      ),

    billing_notes: optionalText(),

    billing_contact_name: optionalText(),
    billing_contact_title: optionalText(),
    billing_contact_email: emailField(),
    billing_contact_phone: optionalText(),
  })
  .refine(
    (values) => {
      // Only checkable here when both dates are on the form. With one of them
      // stored and the other sent, the backend does the comparing.
      if (!values.contract_start_date || !values.contract_end_date) return true;
      // A contract lasting a single day is a contract, so equal dates pass.
      return values.contract_end_date >= values.contract_start_date;
    },
    {
      message: CONTRACT_DATES_MESSAGE,
      path: ['contract_end_date'],
    },
  );

/** True when a refusal is about the contract dates rather than anything else. */
export function isContractDateRefusal(message: string): boolean {
  return message.includes('contract_end_date');
}

export type BillingFormValues = z.input<typeof billingFormSchema>;
export type BillingFormOutput = z.output<typeof billingFormSchema>;
export type BillingDirtyFields = DirtyFieldMap<keyof BillingFormValues>;

export const EMPTY_BILLING: BillingFormValues = {
  plan_name: '',
  plan_cost: '',
  plan_currency: DEFAULT_CURRENCY,
  plan_billing_cycle: '',
  contract_start_date: '',
  contract_end_date: '',
  renewal_date: '',
  invoice_status: '',
  payment_status: '',
  last_invoice_number: '',
  billing_notes: '',
  billing_contact_name: '',
  billing_contact_title: '',
  billing_contact_email: '',
  billing_contact_phone: '',
};

/**
 * Turn a stored account into the flat shape the billing form holds.
 *
 * The cost is carried across as the string the backend sent, already rounded to
 * two places by it. Every absent value becomes an empty string rather than
 * staying null, because a React input given null is uncontrolled and loses the
 * form's record of what changed at the first keystroke.
 */
export function toBillingFormValues(record: OrganizationResponse): BillingFormValues {
  return {
    plan_name: record.plan_name ?? '',
    plan_cost: record.plan_cost ?? '',
    plan_currency: record.plan_currency ?? DEFAULT_CURRENCY,
    plan_billing_cycle: record.plan_billing_cycle ?? '',
    contract_start_date: record.contract_start_date ?? '',
    contract_end_date: record.contract_end_date ?? '',
    renewal_date: record.renewal_date ?? '',
    invoice_status: record.invoice_status ?? '',
    payment_status: record.payment_status ?? '',
    last_invoice_number: record.last_invoice_number ?? '',
    billing_notes: record.billing_notes ?? '',
    billing_contact_name: contactPart(record.billing_contact, 'name'),
    billing_contact_title: contactPart(record.billing_contact, 'title'),
    billing_contact_email: contactPart(record.billing_contact, 'email'),
    billing_contact_phone: contactPart(record.billing_contact, 'phone'),
  };
}

/**
 * Turn an edited billing form into a partial update, carrying only what changed.
 *
 * The same rule as the details form, and for the same reason: sending the whole
 * form cannot tell "left alone" from "cleared", and would overwrite a
 * colleague's edit to a field this form does not even show.
 *
 * `stored` is the billing contact as it is held now: the address it carries,
 * which no staff form shows, goes back with every edit to it.
 */
export function toBillingUpdatePayload(
  values: BillingFormOutput,
  dirtyFields: BillingDirtyFields,
  stored: OrganizationResponse['billing_contact'],
): OrganizationUpdate {
  const payload: OrganizationUpdate = {};

  /*
   * The backend refuses to clear the currency, and the form refuses a blank one
   * before anything is sent. The guard stays because this is the last thing
   * between the form and the request, and a null here comes back as a 422 that
   * reads like a fault rather than a typo.
   */
  if (isFieldDirty(dirtyFields, 'plan_currency') && values.plan_currency) {
    payload.plan_currency = values.plan_currency;
  }

  // An empty string is the only falsy value any of these can hold, which is
  // exactly the case that should clear the field.
  if (isFieldDirty(dirtyFields, 'plan_name')) {
    payload.plan_name = values.plan_name || null;
  }
  if (isFieldDirty(dirtyFields, 'plan_cost')) {
    // Sent as typed. The backend parses the decimal and rounds it to the penny.
    payload.plan_cost = values.plan_cost || null;
  }
  if (isFieldDirty(dirtyFields, 'plan_billing_cycle')) {
    payload.plan_billing_cycle =
      (values.plan_billing_cycle as OrganizationUpdate['plan_billing_cycle']) || null;
  }
  if (isFieldDirty(dirtyFields, 'contract_start_date')) {
    payload.contract_start_date = values.contract_start_date || null;
  }
  if (isFieldDirty(dirtyFields, 'contract_end_date')) {
    payload.contract_end_date = values.contract_end_date || null;
  }
  if (isFieldDirty(dirtyFields, 'renewal_date')) {
    payload.renewal_date = values.renewal_date || null;
  }
  if (isFieldDirty(dirtyFields, 'invoice_status')) {
    payload.invoice_status =
      (values.invoice_status as OrganizationUpdate['invoice_status']) || null;
  }
  if (isFieldDirty(dirtyFields, 'payment_status')) {
    payload.payment_status =
      (values.payment_status as OrganizationUpdate['payment_status']) || null;
  }
  if (isFieldDirty(dirtyFields, 'last_invoice_number')) {
    payload.last_invoice_number = values.last_invoice_number || null;
  }
  if (isFieldDirty(dirtyFields, 'billing_notes')) {
    payload.billing_notes = values.billing_notes || null;
  }

  /*
   * The contact is stored as one thing, so it is replaced as one thing. Editing
   * only the phone number and sending only the phone number would drop the name
   * and the email address with it. The same helpers as the profile and the
   * Super Admin's billing page, so the three cannot disagree (review item 44).
   */
  const contactTouched = CONTACT_FIELDS.some((field) => isFieldDirty(dirtyFields, field));
  if (contactTouched) {
    payload.billing_contact = keepStoredAddress(
      contactToPayload({
        name: values.billing_contact_name,
        title: values.billing_contact_title,
        email: values.billing_contact_email,
        phone: values.billing_contact_phone,
      }),
      stored,
    );
  }

  return payload;
}
